import { apiClient } from '@shared/api/client';
import type { Receipt, ReceiptExtraction } from '../types';

/**
 * Accès à l'API backend des receipts et de l'analyse IA.
 * Les formes de réponse correspondent aux schémas Zod du serveur.
 */

interface AnalyzeResponse {
  extraction: ReceiptExtraction;
}

interface ListReceiptsResponse {
  items: Receipt[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/** Payload de création : les champs validés + métadonnées du fichier. */
export type CreateReceiptPayload = Omit<Receipt, 'id' | 'createdAt' | 'previewUrl' | 'status'>;

/** Analyse localement un document via le pipeline OCR du serveur. */
export async function analyzeReceipt(file: File): Promise<ReceiptExtraction> {
  const body = new FormData();
  body.append('document', file, file.name);
  body.append('fileName', file.name);
  body.append('fileType', file.type);
  const { extraction } = await apiClient.postForm<AnalyzeResponse>('/ai/analyze', body);
  return extraction;
}

/** Liste les receipts persistés (les plus récents en premier). */
export async function fetchReceipts(): Promise<Receipt[]> {
  const { items } = await apiClient.get<ListReceiptsResponse>('/receipts?pageSize=100');
  return items;
}

/** Persiste un ticket validé. */
export async function createReceipt(payload: CreateReceiptPayload): Promise<Receipt> {
  return apiClient.post<Receipt>('/receipts', payload);
}

/** Supprime un ticket. */
export async function deleteReceipt(id: string): Promise<void> {
  await apiClient.delete(`/receipts/${id}`);
}

/** Change le statut d'un ticket (valider / remettre en revue). */
export async function setReceiptStatus(
  id: string,
  status: 'validated' | 'needs_review',
): Promise<Receipt> {
  return apiClient.patch<Receipt>(`/receipts/${id}/status`, { status });
}
